import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { any, equals, filter, head, insertAll, isEmpty, keys, last, reverse, uniq } from 'ramda';
import { debounce } from 'lodash';

import { isMobile } from 'react-device-detect';

import classnames from 'classnames';

import ChatPanelOverview from '../ChatPanelOverview/ChatPanelOverviewContainer';
import MessagesList from '../MessagesList/MessagesListContainer';
import MessageInput from '../MessageInput/MessageInputContainer';
import LandingScreen from '../LandingScreen/LandingScreenContainer';
import LoadingEllipsis from '../LoadingEllipsis';
import { getSelectedMessageCount, isDirectMessage } from '../../helpers/ChatUtils';
import { X } from 'react-feather';

import { CHANNEL_DOESNT_EXIST } from '../../constants/StringConstants';

/**
 * Number of pixels away from the bottom of the chat panel that is treated as at the bottom.
 */
const AT_SCROLL_BOTTOM_BUFFER = 100;

const TOP_BAR_OFFSET = 65;
const NEW_MESSAGE_SEPARATOR_HEIGHT = 32;

/** Used as the main chat window area where messages are displayed. */
export default class ChatPanel extends Component {
  static propTypes = {
    chatId: PropTypes.number,
    chat: PropTypes.shape({
      id: PropTypes.number,
      users: PropTypes.array,
      name: PropTypes.string,
      type: PropTypes.string,
      lastMessageRead: PropTypes.number,
      unreadCount: PropTypes.number,
      /*
       * chat.messages array example:
       * [324, 327]
       */
      messages: PropTypes.array,
      userMentioned: PropTypes.bool,
    }),
    users: PropTypes.object,
    getMessages: PropTypes.func.isRequired,
    clearUnreadCount: PropTypes.func.isRequired,
    join: PropTypes.bool,
    showChat: PropTypes.func,
    markAsRead: PropTypes.func,
    /*
    messages example object:
    {
      324: {
        attachments: [],
        chatId: 8,
        content: "dawdaw",
        contentType: "text/plain",
        id: 324,
        senderId: 8,
        timeSent: "2018-11-29T20:43:56.175Z",
      }, 327: {
        attachments: [],
        chatId: 8,
        content: "dawdaw",
        contentType: "text/plain",
        id: 327,
        senderId: 8,
        timeSent: "2018-11-29T20:43:56.175Z",
      },
    }
    */
    messages: PropTypes.object,
    selectedMessages: PropTypes.object,
    scrollPosition: PropTypes.number,
    saveScrollPosition: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);
    this.panelContent = React.createRef();
    const { messages, identityId, chat, selectedMessages } = this.props;
    this.state = {
      firstUnreadMessage: chat ? this.findFirstUnreadMessage(messages, chat.lastMessageRead, identityId) : null,
      loadingChat: false,
      loadingMoreMessages: false,
      usersTypingText: null,
      usersTypingPostfix: null,
      unreadMessagesWhileInChatIds: [],
      hideUnreadMessageBar: false,
      userMentionedInUnreadMessages: false,
      isSelectingMessages: getSelectedMessageCount(selectedMessages) > 0,
    };
    this.handleScrollDebounced = debounce(this.handleScroll, 500);
  }

  findFirstUnreadMessage(messages, lastMessageReadId, identityId) {
    const messageIds = keys(messages);
    const lastMessageId = this.findLastMessageId(messages);

    if (lastMessageId && lastMessageReadId && lastMessageId !== lastMessageReadId) {
      const messageAfterLastMessageRead = messages[messageIds[messageIds.indexOf(lastMessageReadId.toString()) + 1]];
      // We don't want to show our own message as a new message
      if (messageAfterLastMessageRead && messageAfterLastMessageRead.senderId !== identityId) {
        return messageAfterLastMessageRead;
      }
    }

    return null;
  }

  componentDidMount() {
    const { chat, chatId, messages, join, joinChannel, publicChannels, showChat, getMessages } = this.props;

    // Three scenarios need covered upon chat panel mount:
    // 1: Click an existing chat for first time and it mounts with 0 messages
    // 2: Click +DM and send a message.  Chat Panel mounts with 1 message
    // 3: If the mounting occurs on a join, don't dispatch getMessages.  We do that inside the join saga already
    if (!join && (keys(messages).length <= 1 || (chat && chat.unreadCount > 0))) {
      this.setState({ loadingChat: true });
      getMessages(chatId);
    }

    // Clear unread count if the user goes directly to a chat URL with unread messages
    // this.clearUnreadCountForActiveChat(chat); // Commented out 9/25/2019, this was causing an issue where the first message of a chat would not be marked as the last message read,
    // This is because when we get to the hasUnreadMessages check, the unread message count was already cleared.
    // Going directly to chat url will now not work correctly, but, this is not something that should be done under normal circumstances.

    // If we are joining a new or existing channel...
    if (join) {
      this.setState({ loadingChat: true });

      // the join prop gets passed in directly from RouterSwitch.js via <ChatPanel join />
      // only dispatch an action to join the channel if it is not already in state.chats,
      // the channel exists in state.publicChannels, and you are on the /chat/[id]/join route
      if ( publicChannels[chatId] && !chat) {
        joinChannel(chatId);
      }
    }

    // We only can hide/show DMs
    isDirectMessage(chat) && chat.hidden && showChat([chatId]);

    chat && this.chatPostRender();
  }

  componentWillUnmount() {
    const contentArea = this.getContentArea();
    contentArea.removeEventListener('scroll', this.handleScrollDebounced);
  }

  componentDidUpdate(prevProps, prevState) {
    const { loadingChat, loadingMoreMessages, userMentionedInUnreadMessages } = this.state;
    const { chat, chatId, messages, markAsRead, identityId, getMessages, scrollPosition, join, selectedMessages } = this.props;
    const prevChatId = prevProps.chatId;

    const isDifferentChat = chatId && chatId !== prevChatId;
    const isSameChat = chatId && chatId === prevChatId;
    const hasUnreadMessages = chat && chat.unreadCount > 0;
    const chatHasMessages = chat && chat.messages && chat.messages.length > 0;
    const hasDifferentMessages = !equals(prevProps.messages, messages);
    const didReceiveNewMessages = isSameChat
      && hasDifferentMessages
      && prevProps.chat && chat.lastMessageRead
      && any(messageId => (messageId > chat.lastMessageRead && messages[messageId].senderId !== identityId), chat.messages);

    // GetMessages if we have none of them, the prev prop check is needed because of multiple updates occur
    if (isDifferentChat && !chatHasMessages) {
      getMessages(chatId);
      this.setState({ loadingChat: true });
    }

    /** ************************ Loading State Work Begin***************************/
    if (
      loadingChat &&
      (chatHasMessages || (prevProps.join && !join))
    ) {
      this.setState({ loadingChat: false }, () => {
        this.scrollToBottomOfChat();
      });
    }

    // If user routes to a different chat, set to loading since the component is already mounted
    if (isDifferentChat && !chatHasMessages) {
      this.setState({ loadingChat: true });
    }

    // Show loading state for Get More Messages
    if (loadingMoreMessages && chat.messages.length > prevProps.chat.messages.length) {
      this.setState({ loadingMoreMessages: false });
    }
    /** ************************ Loading State Work End***************************/

    if (chat && prevProps && prevProps.chat && !equals(chat.typingUsers, prevProps.chat.typingUsers)) {
      this.setUsersTyping();
    }

    if (isDifferentChat) {
      // When switching chats, scroll to the saved scroll position unless there are new messages, in which case always scroll to the bottom.
      if (scrollPosition && chat.unreadCount === 0) {
        this.scrollToPosition(scrollPosition);
      } else {
        this.scrollToBottomOfChat();
      }
      this.setState({
        isSelectingMessages: getSelectedMessageCount(selectedMessages) > 0,
        unreadMessagesWhileInChatIds: [],
        firstUnreadMessage: this.findFirstUnreadMessage(messages, chat.lastMessageRead, identityId),
      });
    }

    if (didReceiveNewMessages && !this.isScrollAtBottom()) {
      const prevUnreadMessagesIds = this.state.unreadMessagesWhileInChatIds;
      const newMessageIds = filter(messageId => (messageId > messages[chat.lastMessageRead].id && (prevUnreadMessagesIds.length === 0 || messageId > prevUnreadMessagesIds[prevUnreadMessagesIds.length - 1]) && messages[messageId].senderId !== identityId), chat.messages);
      const unreadMessagesWhileInChatIds = insertAll(prevUnreadMessagesIds.length, newMessageIds, prevUnreadMessagesIds);
      this.setState({
        hideUnreadMessageBar: false,
        unreadMessagesWhileInChatIds,
        firstUnreadMessage: messages[head(unreadMessagesWhileInChatIds)],
      });
    }

    /* The !userMentionedInUnreadMessages is so that we don't get stuck setting this to true in an infinite loop.
     * chat.userMentioned will become false due to the markAsRead call at the bottom, but because that is a saga call it will not happen in time to use
     * it as the break condition for this check.
     */
    if (isSameChat && chat && chat.userMentioned && !userMentionedInUnreadMessages && !this.isScrollAtBottom()) {
      this.setState({
        userMentionedInUnreadMessages: true,
      });
    }

    const usersTypingChanged = !equals(prevState.usersTypingText, this.state.usersTypingText) || !equals(prevState.usersTypingPostfix, this.state.usersTypingPostfix);
    if (isSameChat && (hasDifferentMessages || usersTypingChanged)) {
      // If the user is scrolled to the bottom of chat, then it should continue to stay at the bottom when they receive or send messages
      if (this.isScrollAtBottom()) {
        this.scrollToBottomOfChat();
      }
    }

    // If we just loaded a chat that has unread messages or we received new messages while in the chat
    if (chatHasMessages && (hasUnreadMessages || didReceiveNewMessages)) {
      this.clearUnreadCountForActiveChat(chat);
      const lastReadMessage = reverse(Object.values(messages)).find((message) => {
        const id = message._id || message.id;
        return !isNaN(parseInt(id, 10));
      });
      markAsRead(chatId, [parseInt(lastReadMessage._id || lastReadMessage.id, 10)]);
    }

    chat && !prevProps.chat && this.chatPostRender();
  }

  // When you navigate directly to a chat, the component
  // mounts prior to the Redux store having the chat state.
  // Immediately after we have the chat, we want this run,
  // whether its on mount or on component update
  chatPostRender = () => {
    // Debounce so we only call this once scrolling has stopped for 500ms, that way we don't all it 100 times every scroll
    this.panelContent.current.addEventListener('scroll', this.handleScrollDebounced);

    // Needed if: 1) you have loaded the chat and no updates
    // 2) go to a new route like, create channel
    // 3) go back to chat and it remounts
    this.scrollToBottomOfChat();

  };

  findLastMessageId = messages => isEmpty(messages) ? null : last(keys(messages));

  clearUnreadCountForActiveChat = (chat) => {
    if (chat && chat.unreadCount > 0) {
      this.props.clearUnreadCount(chat.id);
    }
  };

  getContentArea = () => this.panelContent.current;

  handleScroll = () => {
    this.saveScrollPosition();
    if (this.isFirstUnreadMessageInViewOrAbove()) {
      this.clearUnreadMessages();
    }
  };

  saveScrollPosition = () => {
    const { chatId, saveScrollPosition } = this.props;
    const contentArea = this.getContentArea();
    const scrollTop = contentArea.scrollTop;
    saveScrollPosition(chatId, scrollTop);
  };

  /**
   * Helper method to see if the user is scrolled to the bottom of the chat
   */
  isScrollAtBottom = () => {
    const { messages } = this.props;
    let lastMsgEle = null;

    // Get the last message's height dynamically
    if (!isEmpty(messages)) {
      const lastMessageId = this.findLastMessageId(messages);
      lastMsgEle = document.getElementById(`message-${lastMessageId}`);
    }

    const contentArea = this.getContentArea();
    const contentAreaScrollHeight = contentArea.scrollHeight;
    const contentAreaHeight = contentArea.offsetHeight;
    const scrollPos = contentArea.scrollTop;
    const lastEleHeight = lastMsgEle ? lastMsgEle.scrollHeight : 0;

    return scrollPos + contentAreaHeight + AT_SCROLL_BOTTOM_BUFFER >= contentAreaScrollHeight - lastEleHeight;
  };

  scrollToBottomOfChat = () => {
    const contentArea = this.getContentArea();
    contentArea.scrollTop = contentArea.scrollHeight;
  };

  scrollToPosition = (scrollTop) => {
    const contentArea = this.getContentArea();
    contentArea.scrollTop = scrollTop;
  };

  scrollToFirstUnreadMessage = () => {
    const { firstUnreadMessage } = this.state;
    const messageElement = this.findMessageHtmlElement(firstUnreadMessage.id);
    if (messageElement) {
      this.scrollToPosition(messageElement.offsetTop - TOP_BAR_OFFSET - NEW_MESSAGE_SEPARATOR_HEIGHT);
    }
    this.clearUnreadMessages();
  };

  isFirstUnreadMessageInViewOrAbove = () => {
    const { firstUnreadMessage } = this.state;
    return firstUnreadMessage
      ? this.isMessageInViewOrAbove(firstUnreadMessage.id)
      : undefined;
  };

  isMessageInViewOrAbove = (messageId) => {
    const contentArea = this.getContentArea();
    const contentAreaTop = contentArea.scrollTop;
    const contentAreaHeight = contentArea.offsetHeight;
    const messageElement = this.findMessageHtmlElement(messageId);
    const messageTop = messageElement.offsetTop;
    const messageHeight = messageElement.offsetHeight;
    return (messageTop + messageHeight) <= (contentAreaTop + contentAreaHeight);
  };

  findMessageHtmlElement = messageId => messageId
    ? document.getElementById('message-' + messageId)
    : null;

  hasMorePages = (chatId) => {
    if (!this.props.messagesPaging[chatId]) return false;
    const chatPaging = this.props.messagesPaging[chatId];
    return chatPaging.lastRequestedPage < chatPaging.totalPages - 1;
  };

  getNextPageOfMessages = (chatId) => {
    this.setState({ loadingMoreMessages: true });
    if (this.hasMorePages(chatId)) {
      const { lastRequestedPage } = this.props.messagesPaging[chatId];
      const nextPage = lastRequestedPage + 1;
      this.props.getMessages(chatId, nextPage);
    }
  };

  setUsersTyping = () => {
    const { chat, users } = this.props;
    const typingUsers = (chat && chat.typingUsers && uniq(chat.typingUsers)) || [];

    let usersTypingText, usersTypingPostfix;
    if (typingUsers.length > 1) {
      usersTypingText = 'Multiple people';
      usersTypingPostfix = 'are typing';
    } else if (typingUsers.length === 1) {
      const singleUserTyping = users[typingUsers[0]];
      usersTypingText = singleUserTyping && `${singleUserTyping.firstName} ${singleUserTyping.lastName}`;
      usersTypingPostfix = 'is typing';
    }

    this.setState({
      usersTypingText,
      usersTypingPostfix,
    });
  };

  hideUnreadMessageBar = () => this.setState({ hideUnreadMessageBar: true });
  clearUnreadMessages = () => this.setState({
    unreadMessagesWhileInChatIds: [],
    hideUnreadMessageBar: false,
    userMentionedInUnreadMessages: false,
  });

  shouldShowUnreadMessageBar = () => this.state.unreadMessagesWhileInChatIds.length > 0 && !this.state.hideUnreadMessageBar;

  setSelectMessages = val => this.setState({ isSelectingMessages: val });

  render() {
    const { chatId, chat, messages, publicChannels, join, selectedMessages } = this.props;
    const { loadingChat, loadingMoreMessages, firstUnreadMessage, unreadMessagesWhileInChatIds,
      userMentionedInUnreadMessages,
      usersTypingText, usersTypingPostfix, isSelectingMessages } = this.state;

    return (
      <Fragment>
        {chat ? (
          <div data-qa="chatPanel" className="chat-panel">
            <ChatPanelOverview
              chat={chat}
              setSelectMessages={this.setSelectMessages}
              isSelectingMessages={isSelectingMessages}
              selectedMessageCount={getSelectedMessageCount(selectedMessages)}
            />
            {this.shouldShowUnreadMessageBar() &&
              <div
                className={classnames({
                  'unread-message-bar': true,
                  'mentioned': userMentionedInUnreadMessages,
                })}
              >
                <button
                  className="message"
                  onClick={this.scrollToFirstUnreadMessage}
                >
                  <span>{unreadMessagesWhileInChatIds.length} New Messages Below</span>
                </button>
                <button
                  className="close-button"
                  onClick={this.hideUnreadMessageBar}
                >
                  <X size="20"></X>
                </button>
              </div>
            }
            <div
              className="panel-content"
              ref={this.panelContent}
            >
              { loadingMoreMessages ? (
                <div className="load-more-messages-container">
                  <span className="loading-indicator small"/>
                </div>
              ) : (
                this.hasMorePages(chatId) &&
                  <button
                    onClick={() => this.getNextPageOfMessages(chatId)}
                    className="load-more-dialog"
                  >
                    Load Earlier Messages
                  </button>
              )}
              { loadingChat ? (
                <div data-qa="loadingState" className="loading-state">
                  <span className="loading-indicator"/>
                </div>
              ) : (
                <MessagesList
                  messages={messages}
                  selectedMessages={selectedMessages}
                  chat={chat}
                  firstUnreadMessageId={firstUnreadMessage ? ('' + firstUnreadMessage.id) : null }
                  isSelectingMessages={isSelectingMessages}
                />
              )}
            </div>
            {usersTypingText &&
              <UsersTyping
                usersTypingText={usersTypingText}
                usersTypingPostfix={usersTypingPostfix}
              />
            }
            <MessageInput
              chatId={chatId}
              autoFocus={!isMobile}
              onSendMessage={this.scrollToBottomOfChat}
            />
          </div>
        ) : (
          <Fragment>
            {join ? (
              <Fragment>
                {publicChannels[chatId] ? (
                  <div className="loading-state" data-qa="joiningChannelMessage">
                    <span className="loading-indicator"/>
                  </div>
                ) : (
                  <div className="loading-state" data-qa="cannotJoinChannelMessage">
                    <h3>{CHANNEL_DOESNT_EXIST}</h3>
                  </div>
                )}
              </Fragment>
            ) : (
              <LandingScreen />
            )}
          </Fragment>
        )}
      </Fragment>
    );
  }
}

const UsersTyping = ({ usersTypingText, usersTypingPostfix }) => {
  return (
    <div className="typing-container">
      <div className="typing-text">{usersTypingText}</div>
      <div className="typing-postfix">{usersTypingPostfix}</div>
      <LoadingEllipsis />
    </div>
  );
};
